import React, { Component } from 'react';
import { BrowserRouter } from 'react-router-dom';
import * as am4core from '@amcharts/amcharts4/core';
import am4ThemesAnimated from '@amcharts/amcharts4/themes/animated';
import LogRocket from 'logrocket';

import { ToastProvider, ModalProvider } from '@trqk/components';
import {
    AnchorLinksProvider,
    LogDataProvider,
    TasksProvider,
} from '@trqk/hooks';
import {
    addPolyfills,
    getWebpackEnvVar,
    shouldEnableLogRocket,
} from '@trqk/utils';

import { A11yWrapper, ApolloWrapper, IdentifyUser } from './wrappers';
import {
    AnchorLinksContext,
    DetectionTracksContext,
    DetectionTracksProvider,
    EntityContext,
    EntityProvider,
    ExportDetectionsContext,
    ExportDetectionsProvider,
    GapAnalysisToolsContext,
    GapAnalysisToolsProvider,
    LogDataContext,
    ModalContext,
    PaymentPlanStatusContext,
    PaymentPlanStatusProvider,
    StatementSelectionContext,
    StatementSelectionProvider,
    TasksContext,
    ToastContext,
    UploadAudioFilesContext,
    UploadAudioFilesProvider,
    UploadDetectionsContext,
    UploadDetectionsProvider,
    UploadStatementsContext,
    UploadStatementsProvider,
    UploadTracksContext,
    UploadTracksProvider,
} from '../hooks';
import { Routes } from '.';
import { EntitySelector } from '../components/menus';
import {
    AppVersionStatus,
    // CheckForScriptBlocker,
    CheckPaymentPlanStatus,
    DataMigrationStatus,
    EnableDetectionReminder,
} from '../components/modals';
import {
    ANCHOR_SCROLL_DELAY,
    ANCHOR_LINK_RULES,
    LOCAL_STORAGE_KEYS,
    MAIN_CONTENT_ID,
    PUBLIC_VERSION,
} from '../constants';

import '@trqk/styles/src/main.css';
import '@trqk/styles/src/trqk-table.css';
import 'typeface-montserrat';

const amChartsLicense = getWebpackEnvVar('web', 'AMCHARTS_LICENSE_CHARTS');
const amChartsMapLicense = getWebpackEnvVar('web', 'AMCHARTS_LICENSE_MAPS');

am4core.addLicense(amChartsMapLicense);
am4core.addLicense(amChartsLicense);
am4core.useTheme(am4ThemesAnimated);
am4core.options.autoDispose = true;
am4core.options.deferredDelay = 200;

type AppProps = Record<string, unknown>;

interface AppState {
    addedPolyfills: boolean;
}

const enableLogRocket = shouldEnableLogRocket();

export class App extends Component<AppProps, AppState> {
    constructor(props: AppProps) {
        super(props);
        this.state = {
            addedPolyfills: false,
        };

        if (enableLogRocket) {
            LogRocket.init(getWebpackEnvVar('web', 'LOG_ROCKET_APP_ID'), {
                release: PUBLIC_VERSION,
            });
        }
    }

    async componentDidMount(): Promise<void> {
        await addPolyfills('en');

        this.setState({
            addedPolyfills: true,
        });

        return undefined;
    }

    render(): JSX.Element {
        if (!this.state.addedPolyfills) {
            return <></>;
        }

        return (
            <ToastProvider
                toastBannerRootId="toast-banner-root"
                toastCardRootId="toast-card-root"
                ToastContext={ToastContext}
            >
                <ApolloWrapper>
                    <BrowserRouter>
                        <AnchorLinksProvider
                            AnchorLinksContext={AnchorLinksContext}
                            delay={ANCHOR_SCROLL_DELAY}
                            mainContentId={MAIN_CONTENT_ID}
                            rules={ANCHOR_LINK_RULES}
                        >
                            <EntityProvider EntityContext={EntityContext}>
                                {/*
                                    EntitySelector needs to be a sibling element of '#modal-root' so
                                    that z-index has the intended effect.
                                */}
                                <EntitySelector />
                                <div id="modal-root" />
                                <TasksProvider TasksContext={TasksContext}>
                                    <LogDataProvider
                                        LogDataContext={LogDataContext}
                                        scriptBlockerFlagsLocalStorageKey={
                                            LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS
                                        }
                                    >
                                        <StatementSelectionProvider
                                            StatementSelectionContext={
                                                StatementSelectionContext
                                            }
                                        >
                                            <ToastProvider
                                                toastBannerRootId="toast-banner-root"
                                                toastCardRootId="toast-card-root"
                                                ToastContext={ToastContext}
                                            >
                                                <UploadStatementsProvider
                                                    UploadStatementsContext={
                                                        UploadStatementsContext
                                                    }
                                                >
                                                    <PaymentPlanStatusProvider
                                                        PaymentPlanStatusContext={
                                                            PaymentPlanStatusContext
                                                        }
                                                    >
                                                        <DetectionTracksProvider
                                                            DetectionTracksContext={
                                                                DetectionTracksContext
                                                            }
                                                        >
                                                            <UploadTracksProvider
                                                                UploadTracksContext={
                                                                    UploadTracksContext
                                                                }
                                                            >
                                                                <UploadAudioFilesProvider
                                                                    UploadAudioFilesContext={
                                                                        UploadAudioFilesContext
                                                                    }
                                                                >
                                                                    <UploadDetectionsProvider
                                                                        UploadDetectionsContext={
                                                                            UploadDetectionsContext
                                                                        }
                                                                    >
                                                                        <GapAnalysisToolsProvider // this must be within PaymentPlanStatusProvider
                                                                            GapAnalysisToolsContext={
                                                                                GapAnalysisToolsContext
                                                                            }
                                                                        >
                                                                            <ExportDetectionsProvider
                                                                                ExportDetectionsContext={
                                                                                    ExportDetectionsContext
                                                                                }
                                                                            >
                                                                                {/*
                                                                                    Do not move ModalProvider up or down
                                                                                    in the tree unless you really know
                                                                                    what you're doing. Many components
                                                                                    depend on it being in a certain
                                                                                    place in the hierarchy.
                                                                                */}
                                                                                <ModalProvider
                                                                                    modalRootId="modal-root"
                                                                                    ModalContext={
                                                                                        ModalContext
                                                                                    }
                                                                                >
                                                                                    <A11yWrapper>
                                                                                        <DataMigrationStatus>
                                                                                            <AppVersionStatus>
                                                                                                <CheckPaymentPlanStatus>
                                                                                                    {/*
                                                                                                TODO: test for false positives and bring this back once it passes muster
                                                                                                https://app.asana.com/0/1150917131612043/1200693445198179
                                                                                                <CheckForScriptBlocker>
                                                                                            */}
                                                                                                    <EnableDetectionReminder>
                                                                                                        <IdentifyUser>
                                                                                                            <Routes />
                                                                                                        </IdentifyUser>
                                                                                                    </EnableDetectionReminder>
                                                                                                    {/* </CheckForScriptBlocker>  */}
                                                                                                </CheckPaymentPlanStatus>
                                                                                            </AppVersionStatus>
                                                                                        </DataMigrationStatus>
                                                                                    </A11yWrapper>
                                                                                </ModalProvider>
                                                                            </ExportDetectionsProvider>
                                                                        </GapAnalysisToolsProvider>
                                                                    </UploadDetectionsProvider>
                                                                </UploadAudioFilesProvider>
                                                            </UploadTracksProvider>
                                                        </DetectionTracksProvider>
                                                    </PaymentPlanStatusProvider>
                                                </UploadStatementsProvider>
                                            </ToastProvider>
                                        </StatementSelectionProvider>
                                    </LogDataProvider>
                                </TasksProvider>
                            </EntityProvider>
                        </AnchorLinksProvider>
                    </BrowserRouter>
                </ApolloWrapper>
            </ToastProvider>
        );
    }
}
